import { styled } from "@mui/material/styles";
import { DreamTableFilterComponent, DreamTableMakeFilter } from "../dreamTableTypes";
import { describeEnumSelection } from "./filterValueStatus";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import Tooltip from "@mui/material/Tooltip";

export const EnumSingleSelectChipFilter = ({
  id,
  label,
  value,
  setValue,
  enumToFriendlyMap,
  optionsToDisable,
  disabledTooltipTitle = "",
}: Parameters<DreamTableFilterComponent>[0] & {
  enumToFriendlyMap: Partial<Record<string, string>>;
  optionsToDisable?: string[];
  disabledTooltipTitle?: React.ReactNode;
}) => {
  const enumEntries = Object.entries(enumToFriendlyMap);

  // Clicking on the already-set option will reset the filter
  const handleChange = (clickedValue: keyof typeof enumToFriendlyMap) => () => {
    if (value === undefined) {
      setValue(clickedValue, clickedValue);
    } else {
      setValue(value !== clickedValue ? clickedValue : undefined, clickedValue);
    }
  };

  return (
    <FilterContainer>
      <Header>{label}</Header>
      <ChipRow>
        {enumEntries.map(([key, friendlyKey]) => {
          const disabled = optionsToDisable?.includes(key);

          return (
            <Tooltip key={key} arrow title={disabled ? disabledTooltipTitle : ""}>
              <StyledChip
                key={key}
                label={friendlyKey}
                disabled={disabled}
                className={value === key ? "selected" : ""}
                onClick={handleChange(key)}
                data-testid={kebabCase(`${id}-option-${friendlyKey}`)}
              />
            </Tooltip>
          );
        })}
      </ChipRow>
    </FilterContainer>
  );
};

export const makeDreamTableEnumSingleSelectChipFilter = (
  enumToFriendlyMap: Record<string, string>
): DreamTableMakeFilter => ({
  FilterComponent: (props) => EnumSingleSelectChipFilter({ enumToFriendlyMap, ...props }),
  describeValue: (value) => describeEnumSelection(enumToFriendlyMap, value),
});

const StyledChip = styled(Chip)`
  background-color: ${({ theme }) => theme.palette.dream.grayLight};
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.selected};
  }

  &.selected {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.common.white};

    &:hover {
      background-color: ${({ theme }) => theme.palette.primary.dark};
    }
  }
`;

const Header = styled(Typography)`
  font-weight: 700;
  font-size: 1.25rem;
`;

const FilterContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChipRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
